export const HTML_IFRAME_SANDBOX =
    'allow-scripts allow-same-origin allow-forms allow-downloads allow-popups allow-modals'

export const HTML_IFRAME_ALLOW = 'camera; microphone; autoplay; display-capture'

export const FTS_HTML_MSG_POST_MESSAGE = 'fts:postMessage'

export const FTS_HTML_COMPONENT_SOURCE = 'fts-html-component'

export const FTS_HTML_EVENT_DISPATCH = 'fts:dispatch'
export const FTS_HTML_EVENT_READY = 'fts:ready'
export const FTS_HTML_EVENT_ERROR = 'fts:error'
export const FTS_HTML_EVENT_DOWNLOAD = 'fts:download'

export type FtsHtmlIframeMessage =
    | { type: typeof FTS_HTML_EVENT_READY }
    | { type: typeof FTS_HTML_EVENT_ERROR; message: string }
    | { type: typeof FTS_HTML_EVENT_DISPATCH; value: unknown }
    | { type: typeof FTS_HTML_EVENT_DOWNLOAD; url: string; filename?: string }

function createBridgeScript(body: string): string {
    return `<script>
(function () {
    var S=${JSON.stringify(FTS_HTML_COMPONENT_SOURCE)};
    function __ftsPost(msg) {
        parent.postMessage(Object.assign({ source: S }, msg), '*');
    }
    ${body}
})();
</script>`
}

const HEAD_INJECTION =
    '<meta http-equiv="Permissions-Policy" content="camera=*, microphone=*"/>' +
    createBridgeScript(`
    var E=${JSON.stringify(FTS_HTML_EVENT_DISPATCH)};
    var SP=${JSON.stringify(FTS_HTML_MSG_POST_MESSAGE)};
    var ERR=${JSON.stringify(FTS_HTML_EVENT_ERROR)};
    var DL=${JSON.stringify(FTS_HTML_EVENT_DOWNLOAD)};
    function __ftsReportError(msg) {
        __ftsPost({ event: ERR, data: String(msg || 'Unknown error') });
    }
    window.onerror = function (_m, _s, _l, _c, err) {
        __ftsReportError(err && err.message ? err.message : _m);
        return true;
    };
    window.addEventListener('unhandledrejection', function (ev) {
        var r = ev.reason;
        __ftsReportError(r && r.message ? r.message : r);
    });
    var __ftsPendingInbound = [];
    window.fts = {
        disabled: false,
        dispatchEvent: function (value) {
            __ftsPost({ event: E, data: value });
        },
        download: function (url, filename) {
            if (!url) return;
            __ftsPost({
                event: DL,
                data: { url: String(url), filename: filename ? String(filename) : undefined },
            });
        },
        onMessage: function (fn) {
            window.__ftsOnMessage = typeof fn === 'function' ? fn : null;
            if (window.__ftsOnMessage) {
                var pending = __ftsPendingInbound.slice();
                __ftsPendingInbound = [];
                pending.forEach(function (m) {
                    window.__ftsOnMessage(m);
                });
            }
        },
    };
    window.addEventListener('message', function (ev) {
        var d = ev.data;
        if (d && typeof d === 'object' && d.type === SP) {
            var value = d.value;
            if (value && typeof value === 'object' && 'disabled' in value) {
                window.fts.disabled = !!value.disabled;
            }
            if (window.__ftsOnMessage) {
                window.__ftsOnMessage(value);
            } else {
                __ftsPendingInbound.push(value);
            }
        }
    });
`)

const FOOT_INJECTION = createBridgeScript(
    `__ftsPost({ event: ${JSON.stringify(FTS_HTML_EVENT_READY)} });`
)

const FRAGMENT_WRAPPER = `<!DOCTYPE html><html><head><meta charset="utf-8"/>${HEAD_INJECTION}</head><body style="margin:0">`

function appendFootInjection(html: string): string {
    if (/<\/body>/i.test(html)) {
        return html.replace(/<\/body>/i, `${FOOT_INJECTION}</body>`)
    }
    return `${html}${FOOT_INJECTION}`
}

export function wrapFtsPostMessage(message: unknown): Record<string, unknown> {
    return { type: FTS_HTML_MSG_POST_MESSAGE, value: message }
}

export function wrapUserHtml(html: string) {
    if (/<html[\s>]/i.test(html)) {
        const withHead = html.includes('<head>')
            ? html.replace('<head>', `<head>${HEAD_INJECTION}`)
            : HEAD_INJECTION + html
        return appendFootInjection(withHead)
    }
    return FRAGMENT_WRAPPER + html + FOOT_INJECTION + '</body></html>'
}

export function isFtsHtmlComponentMessage(data: unknown): boolean {
    return (
        !!data &&
        typeof data === 'object' &&
        (data as { source?: string }).source === FTS_HTML_COMPONENT_SOURCE
    )
}

export function parseIframeMessage(data: unknown): FtsHtmlIframeMessage | null {
    if (!isFtsHtmlComponentMessage(data)) return null

    const { event, data: payload } = data as {
        event?: string
        data?: unknown
    }

    if (event === FTS_HTML_EVENT_READY) return { type: FTS_HTML_EVENT_READY }
    if (event === FTS_HTML_EVENT_ERROR) {
        return {
            type: FTS_HTML_EVENT_ERROR,
            message: typeof payload === 'string' ? payload : String(payload ?? ''),
        }
    }
    if (event === FTS_HTML_EVENT_DISPATCH) {
        return { type: FTS_HTML_EVENT_DISPATCH, value: payload }
    }
    if (event === FTS_HTML_EVENT_DOWNLOAD) {
        if (!payload || typeof payload !== 'object') return null
        const { url, filename } = payload as { url: string; filename?: string }
        if (typeof url !== 'string' || !url) return null
        return {
            type: FTS_HTML_EVENT_DOWNLOAD,
            url,
            filename: typeof filename === 'string' && filename ? filename : undefined,
        }
    }

    return null
}

export async function downloadPublicFile(url: string, filename?: string): Promise<void> {
    const response = await fetch(url)
    if (!response.ok) {
        throw new Error('Network response was not ok')
    }
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename || url.split('/').pop() || ''
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(downloadUrl)
}
